import { CourseDataApi } from "@features/shared/api/course-data.api";
import { FeedbackRequestRecord, ReportIssueRequestRecord } from "@features/shared/entities/lessons";
import { FeedbackSubmitted, ReportSubmitted } from "@features/shared/events";
import { ModelContract, ModuleDescription } from "@features/shared/module-description";
import { combine, createEffect, createEvent, createStore, sample } from "effector";
import { ICurrentCourseContract } from "@features/courses/ui/current";
import { ICurrentLessonContract } from "./current";
import { IAmplitudeFlagsContract } from "@features/amplitude-flags/model";
import { IMetadataContract } from "@features/student/metadata/model";
import { IAppRouteUnitsContract } from "@features/app/route-units";
import { routePathNameChanged } from "@features/app/model";

const rateLesson = createEvent<number>();
const feedbackMessageChanged = createEvent<string>();
const currentLessonFeedbackOpened = createEvent<number>();
const currentLessonFeedbackHidden = createEvent();
const setCurrentLessonFeedbackStep = createEvent<number>();
const currentLessonReportOpened = createEvent<number>();
const currentLessonReportHidden = createEvent();
const selectReportType = createEvent<string>();
const selectFeedbackType = createEvent<string>();
const reportMessageChanged = createEvent<string>();
const trackLessonReport = createEvent();
const recordingOnInterviewScheduled = createEvent();
const recordingOnInterviewComputed = createEvent<string | null>();
const recordingOnInterviewMetaChanged = createEvent<boolean>();
const recordingOnInterviewLocalFlagChanged = createEvent<boolean | null>();
const getRecordingOnInterviewLocalFlagFx = createEffect<void, boolean | null>();
const setRecordingOnInterviewLocalFlagFx = createEffect<boolean, boolean>();

const $currentLessonRating = createStore<null | number>(null)
    .on(rateLesson, (_, rating) => rating)
    .reset(currentLessonFeedbackHidden);
const $currentLessonFeedbackMessage = createStore("")
    .on(feedbackMessageChanged, (s, p) => p)
    .reset(currentLessonFeedbackHidden);
export const $currentLessonIdForFeedback = createStore<null | number>(null)
    .on(currentLessonFeedbackOpened, (s, p) => p)
    .reset(currentLessonFeedbackHidden);
const $currentLessonFeedbackStep = createStore<null | number>(null)
    .on(setCurrentLessonFeedbackStep, (s, p) => p)
    .reset(currentLessonFeedbackHidden, currentLessonReportHidden);
const $currentLessonReport = createStore<null | number>(null)
    .on(currentLessonReportOpened, (s, p) => p)
    .reset(currentLessonReportHidden);
const $currentLessonReportType = createStore<null | string>(null)
    .on(selectReportType, (s, p) => p)
    .reset(currentLessonReportHidden);
const $currentLessonFeedbackType = createStore<null | string>(null)
    .on(selectFeedbackType, (s, p) => p)
    .on(rateLesson, (s, rating) => (rating >= 4 ? null : s))
    .reset(currentLessonFeedbackHidden);
const $currentLessonReportMessage = createStore("")
    .on(reportMessageChanged, (s, p) => p)
    .reset(currentLessonReportHidden);
const $recordingOnInterviewLink = createStore<string | null>(null)
    .on(recordingOnInterviewComputed, (_, link) => link)
    .reset(currentLessonFeedbackHidden);
const $hasRecordingOnInterview = createStore(false)
    .on(recordingOnInterviewMetaChanged, (_, v) => v)
    .on(recordingOnInterviewLocalFlagChanged, (s, v) => (typeof v === "boolean" ? v : s));
const $recordingOnInterviewLocalLoaded = createStore(false).on(getRecordingOnInterviewLocalFlagFx.doneData, () => true);
const $recordingOnInterviewLocalFlag = createStore<boolean | null>(null).on(
    recordingOnInterviewLocalFlagChanged,
    (_, v) => v
);

const trackLessonFeedbackFx = createEffect<
    {
        courseId: number;
        topicId: number;
        lessonId: number;
        commentType: string;
        comment: string;
        rating: number;
    },
    void
>(({ courseId, topicId, lessonId, commentType, comment, rating }) => {
    FeedbackSubmitted({
        courseId,
        topicId,
        lessonId,
        type: "lesson_feedback",
        rating,
        comment,
        commentType
    });
});

const trackLessonReportFx = createEffect<
    {
        courseId: number;
        topicId: number;
        lessonId: number;
        taskId: number | null;
        pageId: number;
        comment: string;
        issueType: string;
    },
    void
>(({ courseId, topicId, lessonId, taskId, pageId, comment, issueType }) => {
    ReportSubmitted({
        courseId,
        topicId,
        lessonId,
        taskId,
        pageId,
        issueType,
        comment
    });
});

const sendLessonFeedbackFx = createEffect(async (data: FeedbackRequestRecord & { commentType: string | null }) => {
    const { commentType, ...rest } = data;
    const response = await CourseDataApi.sendFeedback(rest);
    return response;
});

const sendLessonReportIssueFx = createEffect(async (data: ReportIssueRequestRecord) => {
    const { taskId, ...rest } = data;
    const response = await CourseDataApi.sendRepportIssue(taskId === null ? rest : data);
    return response;
});

sample({
    clock: routePathNameChanged,
    target: currentLessonFeedbackHidden
});

getRecordingOnInterviewLocalFlagFx.use(() => {
    if (typeof window === "undefined") return null;
    const raw = localStorage.getItem("recordingOnInterviewMeta");
    if (raw === null) return null;
    return raw === "true";
});

setRecordingOnInterviewLocalFlagFx.use((has: boolean) => {
    const value = Boolean(has);
    if (typeof window !== "undefined") {
        localStorage.setItem("recordingOnInterviewMeta", value ? "true" : "false");
    }
    return value;
});

const contract = new ModelContract(
    {
        $currentLessonRating,
        $currentLessonFeedbackMessage,
        $currentLessonIdForFeedback,
        $currentLessonFeedbackStep,
        $currentLessonReport,
        $currentLessonReportType,
        $currentLessonReportMessage,
        $currentLessonFeedbackType,
        $recordingOnInterviewLink,
        $hasRecordingOnInterview,
        $recordingOnInterviewLocalLoaded,
        $recordingOnInterviewLocalFlag
    },
    {
        rateLesson,
        feedbackMessageChanged,
        currentLessonFeedbackOpened,
        currentLessonFeedbackHidden,
        setCurrentLessonFeedbackStep,
        currentLessonReportOpened,
        currentLessonReportHidden,
        selectReportType,
        selectFeedbackType,
        reportMessageChanged,
        trackLessonReport,
        recordingOnInterviewScheduled
    },
    {
        trackLessonFeedbackFx,
        sendLessonFeedbackFx,
        trackLessonReportFx,
        sendLessonReportIssueFx,
        getRecordingOnInterviewLocalFlagFx,
        setRecordingOnInterviewLocalFlagFx
    },
    {}
);

export type ILessonFeedbackContract = typeof contract;

export class LessonFeedbackModule extends ModuleDescription<ILessonFeedbackContract> {
    constructor(
        public currentCourseModule: ICurrentCourseContract,
        public currentLessonModule: ICurrentLessonContract,
        public amplitudeFlagsModule: IAmplitudeFlagsContract,
        public metadataModule: IMetadataContract,
        public appRoutesModule: IAppRouteUnitsContract
    ) {
        super();
    }

    getDefaultContract() {
        return contract;
    }

    run() {
        sample({
            clock: trackLessonReport,
            source: {
                currentLesson: this.currentLessonModule.stores.$currentLesson,
                currentCourse: this.currentCourseModule.stores.$currentCourse,
                page: this.currentLessonModule.stores.$currentSectionIndex
            },
            filter: (s) => s.currentLesson !== null && s.currentCourse !== null,
            fn: (s) => ({
                courseId: s.currentCourse?.id ?? 0,
                topicId: s.currentLesson?.topic_id ?? 0,
                lessonId: s.currentLesson?.id ?? 0,
                taskId: null,
                pageId: s.page ?? 0,
                comment: "",
                issueType: ""
            }),
            target: trackLessonReportFx
        });

        const $recordingOnInterviewSource = combine({
            payload: this.amplitudeFlagsModule.stores.$recordingOnInterview,
            rating: $currentLessonRating,
            hasRecording: $hasRecordingOnInterview
        });

        sample({
            source: combine({
                active: this.appRoutesModule.stores.lesson.$isActive,
                loaded: $recordingOnInterviewLocalLoaded
            }),
            filter: ({ active, loaded }) => Boolean(active) && !loaded,
            fn: () => undefined,
            target: getRecordingOnInterviewLocalFlagFx
        });

        sample({
            clock: getRecordingOnInterviewLocalFlagFx.doneData,
            target: recordingOnInterviewLocalFlagChanged
        });

        sample({
            source: combine({
                active: this.appRoutesModule.stores.lesson.$isActive,
                hasRecording: $hasRecordingOnInterview,
                loaded: $recordingOnInterviewLocalLoaded,
                localFlag: $recordingOnInterviewLocalFlag
            }),
            filter: ({ active, hasRecording, loaded, localFlag }) =>
                Boolean(active) && loaded && localFlag === null && !hasRecording,
            target: this.metadataModule.events.refreshMetadataRequested
        });

        sample({
            clock: this.metadataModule.stores.$metadata,
            fn: (metadata) => Boolean(metadata?.recordingOnInterview),
            target: recordingOnInterviewMetaChanged
        });

        sample({
            clock: recordingOnInterviewMetaChanged,
            target: setRecordingOnInterviewLocalFlagFx
        });

        sample({
            clock: setRecordingOnInterviewLocalFlagFx.doneData,
            target: recordingOnInterviewLocalFlagChanged
        });

        sample({
            clock: [
                $currentLessonRating,
                this.amplitudeFlagsModule.stores.$recordingOnInterview,
                $hasRecordingOnInterview
            ],
            source: $recordingOnInterviewSource,
            fn: ({ payload, rating, hasRecording }) => {
                if (hasRecording) return null;
                if (!payload) return null;
                const allowed = Array.isArray(payload.lesson_rating) ? payload.lesson_rating : [payload.lesson_rating];
                if (allowed.some((r) => r === rating) && typeof payload.link === "string" && payload.link.trim()) {
                    return payload.link.trim();
                }
                return null;
            },
            target: recordingOnInterviewComputed
        });

        sample({
            clock: recordingOnInterviewScheduled,
            source: $recordingOnInterviewLink,
            filter: Boolean,
            fn: () => ({
                key: "recordingOnInterview",
                value: JSON.stringify({ booked: true, ts: Date.now() })
            }),
            target: this.metadataModule.events.setMetadataRequested
        });

        sample({
            clock: recordingOnInterviewScheduled,
            target: setRecordingOnInterviewLocalFlagFx.prepend(() => true)
        });
    }
}
